"use client";

import { useState } from "react";
import {
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetClose,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button, SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { IconAvatar } from "@/components/ui/icon-avatar";
import { logo } from "@/lib/logo-dev";
import { type PresetTypeSlug } from "../lib/categories";
import { FOCUS_RING } from "./icon-button";
import { type PresetResponse, type PresetItemResponse } from "./apply";

export interface ConfirmApplyPresetProps {
  preset: PresetResponse | null;
  presetType: PresetTypeSlug;
  showLogo: boolean;
  onCancel: () => void;
  onConfirm: (preset: PresetResponse) => void;
}

const FIELD_SHADOW =
  "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]";

export function ConfirmApplyPreset({
  preset,
  showLogo,
  onCancel,
  onConfirm,
}: ConfirmApplyPresetProps) {
  const [items, setItems] = useState<PresetItemResponse[]>(preset?.items ?? []);
  const [trackedPreset, setTrackedPreset] = useState(preset);

  if (preset !== trackedPreset) {
    setTrackedPreset(preset);
    setItems(preset?.items ?? []);
  }

  const apply = () => {
    if (!preset) return;
    onConfirm({ ...preset, items, item_count: items.length });
  };

  return (
    <Sheet open={!!preset} onOpenChange={(o) => !o && onCancel()} size="sm">
      <SheetHeader>
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <Icon name="IconLayoutDashboard" size={18} className="text-heading" />
          <SheetTitle>Apply Preset</SheetTitle>
        </div>
        <SheetClose
          aria-label="Close apply preset confirmation"
          className={`relative shrink-0 before:absolute before:left-1/2 before:top-1/2 before:size-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] ${FOCUS_RING}`}
        />
      </SheetHeader>

      <div className="flex min-h-0 flex-1 flex-col gap-5 bg-contrast/[0.02] px-4 py-4 dark:bg-contrast/[0.02] sm:px-6 sm:py-6">
        <div
          className={`w-full shrink-0 rounded-lg border border-contrast/5 bg-contrast/3 px-3 py-2 ${FIELD_SHADOW}`}
        >
          <span className="text-sm text-heading">{preset?.title}</span>
        </div>

        <div className="min-h-0 flex-1 w-full overflow-y-auto rounded-lg border border-contrast/10 bg-contrast/[0.02] p-3">
          {items.length === 0 ? (
            <></>
          ) : (
            <div className="flex flex-wrap items-start gap-2">
              {items.map((item) => {
                const logoUrl = showLogo
                  ? logo({ domain: item.domain, name: item.name, size: 32 })
                  : null;
                return (
                  <div
                    key={item.id}
                    className={`flex h-7 items-center gap-[3px] rounded-lg bg-contrast/4 px-1.5 py-0.5 ${FIELD_SHADOW}`}
                  >
                    {logoUrl && (
                      <IconAvatar
                        src={logoUrl}
                        size={16}
                        alt={item.name}
                        className="shrink-0 rounded-[3px]"
                        icon={
                          <Icon
                            name="IconBag"
                            className="text-heading opacity-50"
                          />
                        }
                      />
                    )}
                    <span className="px-0.5 text-sm font-medium text-contrast/85 whitespace-nowrap">
                      {item.name}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setItems((prev) => prev.filter((i) => i.id !== item.id))
                      }
                      aria-label={`Remove ${item.name}`}
                      className={`flex size-3.5 shrink-0 cursor-pointer items-center justify-center rounded text-contrast/40 transition-colors hover:bg-contrast/6 hover:text-contrast/70 ${FOCUS_RING}`}
                    >
                      <Icon name="IconCrossMedium" size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <SheetFooter className="justify-between">
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <SubmitButton
          type="button"
          variant="primary"
          size="sm"
          onClick={apply}
          disabled={items.length === 0}
        >
          Apply
        </SubmitButton>
      </SheetFooter>
    </Sheet>
  );
}
